// ============================================================
// KYC VERIFICATION MODAL - Didit hosted verification
// ============================================================

import React, { useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  VStack,
  HStack,
  Text,
  Box,
  Icon,
  Spinner,
  Alert,
  AlertIcon,
  AlertDescription,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { Shield, CheckCircle, AlertTriangle, Clock, CreditCard, User, FileText } from 'lucide-react';
import PokerButton from '../ui/PokerButton';
import { POKER_COLORS } from '../../theme/designSystem';
import kycService from '../../services/kycService';
import { KYCStatus } from '../../types/complianceTypes';
import { usePaymentsHold } from '../../services/paymentsHoldService';

interface KYCVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  currentStatus: KYCStatus;
  onVerificationStarted?: () => void;
  onVerificationComplete?: () => void;
}

const KYCVerificationModal: React.FC<KYCVerificationModalProps> = ({
  isOpen,
  onClose,
  userId,
  currentStatus,
  onVerificationStarted,
  onVerificationComplete,
}) => {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const paymentsHold = usePaymentsHold();
  const kycPaused = paymentsHold.enabled && paymentsHold.blockKyc;

  // Ask our server to pull the latest result from Didit. Used automatically
  // when the modal opens on a pending record and from the "Check status"
  // button, so a player is never stuck on a spinner after being approved.
  const handleCheckStatus = async () => {
    setChecking(true);
    setError(null);
    try {
      const r = await kycService.syncSession();
      if ('error' in r) { setError(r.error); return; }
      onVerificationComplete?.();
      if (r.status === 'pending') setError('Didit is still reviewing your verification. Check back in a few minutes.');
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    if (isOpen && currentStatus === 'pending') { handleCheckStatus(); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentStatus]);

  const handleStartVerification = async () => {
    if (kycPaused) { setError(paymentsHold.message); return; }
    setLoading(true);
    setError(null);

    try {
      const result = await kycService.createVerificationSession();

      if ('error' in result) {
        setError(result.error);
        return;
      }

      onVerificationStarted?.();

      // Redirect to Didit's hosted verification page
      kycService.redirectToVerification(result.url);
    } catch (err) {
      console.error('Verification error:', err);
      setError('Failed to start verification. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusContent = () => {
    switch (currentStatus) {
      case 'verified':
        return (
          <VStack spacing={4} py={4}>
            <Box
              p={4}
              borderRadius="full"
              bg="rgba(16, 185, 129, 0.2)"
            >
              <Icon as={CheckCircle} boxSize={12} color="#10b981" />
            </Box>
            <Text color="#10b981" fontSize="xl" fontWeight="bold">
              Eligibility confirmed

            </Text>
            <Text color={POKER_COLORS.accentCyan} textAlign="center">
              You're confirmed eligible for sweepstakes prizes. You can now redeem PSS Coins.
            </Text>
          </VStack>
        );

      case 'pending':
        return (
          <VStack spacing={4} py={4}>
            <Box
              p={4}
              borderRadius="full"
              bg="rgba(251, 191, 36, 0.2)"
            >
              <Icon as={Clock} boxSize={12} color="#fbbf24" />
            </Box>
            <Text color="#fbbf24" fontSize="xl" fontWeight="bold">
              Verification Pending
            </Text>
            <Text color={POKER_COLORS.accentCyan} textAlign="center">
              Your verification is being processed. This usually takes a few minutes.
            </Text>
            {checking ? <Spinner color={POKER_COLORS.cyan} /> : (
              <VStack spacing={2} w="100%">
                <PokerButton variant="primary" w="100%" onClick={handleCheckStatus}>
                  Check status
                </PokerButton>
                <PokerButton variant="secondary" size="sm" onClick={handleStartVerification} isLoading={loading}>
                  Start over
                </PokerButton>
                <Text color="gray.500" fontSize="xs" textAlign="center">
                  Already finished with Didit? Tap Check status. Closed the window early? Start over.
                </Text>
              </VStack>
            )}
          </VStack>
        );

      case 'rejected':
        return (
          <VStack spacing={4} py={4}>
            <Box
              p={4}
              borderRadius="full"
              bg="rgba(239, 68, 68, 0.2)"
            >
              <Icon as={AlertTriangle} boxSize={12} color="#ef4444" />
            </Box>
            <Text color="#ef4444" fontSize="xl" fontWeight="bold">
              Verification Failed
            </Text>
            <Text color={POKER_COLORS.accentCyan} textAlign="center">
              Your verification could not be completed. Please try again with clear photos of your ID.
            </Text>
            <PokerButton
              variant="primary"
              onClick={handleStartVerification}
              isLoading={loading}
            >
              Try Again
            </PokerButton>
          </VStack>
        );

      case 'requires_input':
        return (
          <VStack spacing={4} py={4}>
            <Box
              p={4}
              borderRadius="full"
              bg="rgba(251, 191, 36, 0.2)"
            >
              <Icon as={AlertTriangle} boxSize={12} color="#fbbf24" />
            </Box>
            <Text color="#fbbf24" fontSize="xl" fontWeight="bold">
              Additional Information Needed
            </Text>
            <Text color={POKER_COLORS.accentCyan} textAlign="center">
              We need additional information to complete your verification.
            </Text>
            <PokerButton
              variant="primary"
              onClick={handleStartVerification}
              isLoading={loading}
            >
              Continue Verification
            </PokerButton>
          </VStack>
        );

      default: // 'none' or unknown
        return (
          <VStack spacing={6} py={4}>
            <Box
              p={4}
              borderRadius="full"
              bg="rgba(0, 217, 217, 0.2)"
            >
              <Icon as={Shield} boxSize={12} color={POKER_COLORS.cyan} />
            </Box>
            
            <Text color={POKER_COLORS.cyan} fontSize="xl" fontWeight="bold">
              Confirm you're eligible for prizes
            </Text>
            
            <Text color={POKER_COLORS.accentCyan} textAlign="center" px={4}>
              Sweepstakes prizes can only go to players who are 18 or older and located in an
              eligible state. Confirm it once with a government ID; every redemption after that is quick.
            </Text>

            <Box
              w="100%"
              p={4}
              bg={POKER_COLORS.darkBase}
              borderRadius="lg"
              borderLeft={`4px solid ${POKER_COLORS.gold}`}
            >
              <Text color={POKER_COLORS.gold} fontWeight="bold" mb={3}>
                What you'll need:
              </Text>
              <List spacing={2}>
                <ListItem display="flex" alignItems="center" gap={2}>
                  <Icon as={CreditCard} color={POKER_COLORS.cyan} boxSize={4} />
                  <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                    Valid government-issued ID (driver's license, passport, or ID card)
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="center" gap={2}>
                  <Icon as={User} color={POKER_COLORS.cyan} boxSize={4} />
                  <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                    A selfie for face matching
                  </Text>
                </ListItem>
                <ListItem display="flex" alignItems="center" gap={2}>
                  <Icon as={FileText} color={POKER_COLORS.cyan} boxSize={4} />
                  <Text color={POKER_COLORS.accentCyan} fontSize="sm">
                    You must be 18 years or older
                  </Text>
                </ListItem>
              </List>
            </Box>

            <Box
              w="100%"
              p={3}
              bg="rgba(16, 185, 129, 0.1)"
              borderRadius="lg"
            >
              <HStack>
                <Icon as={Shield} color="#10b981" boxSize={5} />
                <Text color="#10b981" fontSize="sm">
                  Secured by Didit, our verification partner. Your data is encrypted and protected.
                </Text>
              </HStack>
            </Box>

            <Text color="gray.500" fontSize="xs" textAlign="center">
              Verification usually takes 1-2 minutes. You'll only need to do this once.
            </Text>

            <PokerButton
              variant="primary"
              size="lg"
              w="100%"
              onClick={handleStartVerification}
              isLoading={loading}
              loadingText="Starting verification..."
            >
               Start Verification

            </PokerButton>
          </VStack>
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <ModalContent 
        bg={POKER_COLORS.darkTeal} 
        borderColor={POKER_COLORS.cyan} 
        border="2px"
        mx={4}
      >
        <ModalHeader color={POKER_COLORS.cyan}>
          Prize eligibility
        </ModalHeader>
        <ModalCloseButton color={POKER_COLORS.cyan} />

        <ModalBody>
          {kycPaused && (
            <Alert status="warning" mb={4} bg="rgba(249, 115, 22, 0.15)" border="1px solid #fb923c" borderRadius="md" alignItems="flex-start">
              <AlertIcon color="#fb923c" />
              <AlertDescription color="white" fontSize="sm">
                <Text fontWeight="bold" mb={1}>ID verification is paused right now</Text>
                {paymentsHold.message}
              </AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert status="error" mb={4} bg="rgba(239, 68, 68, 0.2)" borderRadius="md">
              <AlertIcon color="#ef4444" />
              <AlertDescription color="#ef4444">{error}</AlertDescription>
            </Alert>
          )}

          {getStatusContent()}
        </ModalBody>

        <ModalFooter>
          {currentStatus === 'verified' && (
            <PokerButton variant="secondary" onClick={onClose}>
              Close
            </PokerButton>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default KYCVerificationModal;